import { API_BASE_URL } from "../config";
import { createHttpClient } from "../fetchClient";
import { createAuthenticatedClient } from "../authenticatedClient";
import { safeValidate } from "../safeValidate";
import {
  confirmMfaRequestSchema,
  confirmMfaResponseSchema,
  confirmForgotPasswordRequestSchema,
  confirmForgotPasswordResponseSchema,
  forgotPasswordRequestSchema,
  forgotPasswordResponseSchema,
  loginRequestSchema,
  loginResponseSchema,
  mfaVerificationRequestSchema,
  refreshTokenRequestSchema,
  refreshTokenResponseSchema,
  resetPasswordRequestSchema,
  resetPasswordResponseSchema,
  setupMfaRequestSchema,
  setupMfaResponseSchema,
  ssoCallbackRequestSchema,
  ssoInitiateRequestSchema,
  ssoInitiateResponseSchema,
  logoutResponseSchema,
  type ConfirmMfaRequest,
  type ConfirmMfaResponse,
  type ConfirmForgotPasswordRequest,
  type ConfirmForgotPasswordResponse,
  type ForgotPasswordRequest,
  type ForgotPasswordResponse,
  passwordPolicySchema,
  type PasswordPolicy,
  type LoginRequest,
  type LoginResponse,
  type MfaVerificationRequest,
  type RefreshTokenRequest,
  type RefreshTokenResponse,
  type ResetPasswordRequest,
  type ResetPasswordResponse,
  type SetupMfaRequest,
  type SetupMfaResponse,
  type SSOCallbackRequest,
  type SSOInitiateResponse,
  type LogoutResponse,
} from "../schemas/auth.schema";

const authClient = createHttpClient(`${API_BASE_URL}/api/auth/v1`);
const authenticatedAuthClient = createAuthenticatedClient(
  `${API_BASE_URL}/api/auth/v1`,
);

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const validated = loginRequestSchema.parse(data);
    const response = await authClient.post<LoginResponse>("/login", validated);
    return safeValidate(loginResponseSchema, response, "login");
  },

  verifyMfa: async (data: MfaVerificationRequest): Promise<LoginResponse> => {
    const validated = mfaVerificationRequestSchema.parse(data);
    const response = await authClient.post<LoginResponse>(
      "/mfa/verify",
      validated,
    );
    return safeValidate(loginResponseSchema, response, "verifyMfa");
  },

  refreshToken: async (
    data: RefreshTokenRequest,
  ): Promise<RefreshTokenResponse> => {
    const validated = refreshTokenRequestSchema.parse(data);
    const response = await authClient.post<RefreshTokenResponse>(
      "/refresh",
      validated,
    );
    return safeValidate(refreshTokenResponseSchema, response, "refreshToken");
  },

  resetPassword: async (
    data: ResetPasswordRequest,
  ): Promise<ResetPasswordResponse> => {
    const validated = resetPasswordRequestSchema.parse(data);
    const response = await authClient.put<ResetPasswordResponse>(
      "/password/change",
      validated,
    );
    return safeValidate(resetPasswordResponseSchema, response, "resetPassword");
  },

  setupMfa: async (data: SetupMfaRequest): Promise<SetupMfaResponse> => {
    const validated = setupMfaRequestSchema.parse(data);
    const response = await authClient.post<SetupMfaResponse>(
      "/mfa/setup",
      validated,
    );
    return safeValidate(setupMfaResponseSchema, response, "setupMfa");
  },

  confirmMfa: async (data: ConfirmMfaRequest): Promise<ConfirmMfaResponse> => {
    const validated = confirmMfaRequestSchema.parse(data);
    const response = await authClient.post<ConfirmMfaResponse>(
      "/mfa/confirm",
      validated,
    );
    return safeValidate(confirmMfaResponseSchema, response, "confirmMfa");
  },

  forgotPassword: async (
    data: ForgotPasswordRequest,
  ): Promise<ForgotPasswordResponse> => {
    const validated = forgotPasswordRequestSchema.parse(data);
    const response = await authClient.post<ForgotPasswordResponse>(
      "/password/forgot",
      validated,
    );
    return safeValidate(
      forgotPasswordResponseSchema,
      response,
      "forgotPassword",
    );
  },

  confirmForgotPassword: async (
    data: ConfirmForgotPasswordRequest,
  ): Promise<ConfirmForgotPasswordResponse> => {
    const validated = confirmForgotPasswordRequestSchema.parse(data);
    const response = await authClient.put<ConfirmForgotPasswordResponse>(
      "/password/forgot/confirm",
      validated,
    );
    return safeValidate(
      confirmForgotPasswordResponseSchema,
      response,
      "confirmForgotPassword",
    );
  },

  getPasswordPolicy: async (): Promise<PasswordPolicy> => {
    const response = await authClient.get<PasswordPolicy>("/password/policy");
    return safeValidate(passwordPolicySchema, response, "getPasswordPolicy");
  },

  ssoInitiate: async (email: string): Promise<SSOInitiateResponse> => {
    const validated = ssoInitiateRequestSchema.parse({ email });
    const response = await authClient.post<SSOInitiateResponse>(
      "/sso/initiate",
      validated,
    );
    return safeValidate(ssoInitiateResponseSchema, response, "ssoInitiate");
  },

  ssoCallback: async (data: SSOCallbackRequest): Promise<LoginResponse> => {
    const validated = ssoCallbackRequestSchema.parse(data);
    const response = await authClient.post<LoginResponse>(
      "/sso/callback",
      validated,
    );
    return safeValidate(loginResponseSchema, response, "ssoCallback");
  },

  logout: async (): Promise<LogoutResponse> => {
    const response =
      await authenticatedAuthClient.post<LogoutResponse>("/logout");
    return safeValidate(logoutResponseSchema, response, "logout");
  },
};
